import {onMounted, ref} from 'vue'
import {format} from 'date-fns'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {Draft, useDrafts} from '@/draft/drafts.service'
import {INVOICE_CREATED, INVOICE_DRAFT_CREATED, INVOICE_DRAFT_DELETED, INVOICE_DRAFT_DESELECTED, INVOICE_DRAFT_SELECTED, INVOICE_DRAFT_UPDATED} from '@/types/invoice/InvoiceEvent'

const defaultInvoiceDraftName = () => {
    const date = format(new Date(), 'dd.MM.yyyy\'_\'HH:mm:ss')
    return`Kladd - ${date}`
}

const {onEvent} = useEmitter()
const {saveDraft: originalSaveDraft, updateDraft, deleteDraft, fetchDrafts, loading} = useDrafts()

const invoiceDraftUuid = ref<string>()

export const useInvoiceDrafts = () => {

    const drafts = ref<Draft[]>([])

    const saveDraft = async (draft: Draft) => {
        const savedDraft = await originalSaveDraft(draft)
        invoiceDraftUuid.value = savedDraft.uuid
        return savedDraft
    }

    const invoiceDraftsList = async (): Promise<Draft[]> => {
        return await fetchDrafts('invoice').then(drafts => {
            return drafts.map(draft => ({
                ...draft,
                content: draft.content
            }))
        })
    }

    const getInvoiceDraft = (uuid: string) => {
        return drafts.value.find(draft => draft.uuid === uuid)
    }

    const fetchInvoiceDrafts = async () => {
        drafts.value = await invoiceDraftsList()
    }

    const handleDeleteDraft = async () => {
        if (!invoiceDraftUuid.value) return

        await deleteDraft(invoiceDraftUuid.value)
        await fetchInvoiceDrafts()
    }

    onEvent(ORG_SELECTED, fetchInvoiceDrafts)
    onEvent(INVOICE_DRAFT_SELECTED, (uuid) => invoiceDraftUuid.value = (uuid as string))
    onEvent(INVOICE_DRAFT_DESELECTED, () => invoiceDraftUuid.value = undefined)
    onEvent(INVOICE_DRAFT_CREATED, fetchInvoiceDrafts)
    onEvent(INVOICE_DRAFT_UPDATED, fetchInvoiceDrafts)
    onEvent(INVOICE_DRAFT_DELETED, fetchInvoiceDrafts)

    onEvent(INVOICE_CREATED, handleDeleteDraft)

    onMounted(() => fetchInvoiceDrafts())

    return {
        drafts,
        loading,
        saveDraft,
        updateDraft,
        deleteDraft,
        invoiceDraftsList,
        invoiceDraftUuid,
        getInvoiceDraft
    }
}

export {
    defaultInvoiceDraftName,
}